import React, { useState } from 'react';
import { motion } from 'framer-motion';

const tools = [
  { name: 'WooCommerce', slug: 'woocommerce', color: '#7F54B3', domain: 'woocommerce.com' },
  { name: 'Shopify',     slug: 'shopify',     color: '#96BF48', domain: 'shopify.com' },
  { name: 'HubSpot',     slug: 'hubspot',     color: '#FF7A59', domain: 'hubspot.com' },
  { name: 'Salesforce',  slug: 'salesforce',  color: '#00A1E0', domain: 'salesforce.com', localLogo: '/logos/salesforce.svg' },
  { name: 'Google',      slug: 'google',      color: '#4285F4', domain: 'google.com',     localLogo: '/logos/google.svg' },
  { name: 'WhatsApp',    slug: 'whatsapp',    color: '#25D366', domain: 'whatsapp.com' },
  { name: 'Razorpay',    slug: 'razorpay',    color: '#3395FF', domain: 'razorpay.com' },
  { name: 'Zoho',        slug: 'zoho',        color: '#E42527', domain: 'zoho.com' },
  { name: 'Magento',     slug: 'magento',     color: '#EE672F', domain: 'magento.com',    localLogo: '/logos/magento.svg' },
];

const capabilities = [
  {
    headline: 'Calls, not texts',
    detail: 'A real voice call in the customer\'s own language, at exactly the right moment.',
    bg: 'bg-blue-50',
    accent: 'text-blue-600',
  },
  {
    headline: 'Follows up on schedule',
    detail: 'Auto follow-ups at 24, 48, or 72 hours — set once, runs forever.',
    bg: 'bg-violet-50',
    accent: 'text-violet-600',
  },
  {
    headline: 'Updates, unprompted',
    detail: 'Proactively tells customers where their order is before they even think to ask.',
    bg: 'bg-orange-50',
    accent: 'text-orange-600',
  },
  { 
    headline: 'Handles rescheduling',
    detail: 'Customers can reschedule or redirect a delivery right on the call.',
    bg: 'bg-green-50',
    accent: 'text-green-600',
  },
  { 
    headline: 'Closes payment loops',
    detail: 'Follows up on pending or failed payments and confirms once done.',
    bg: 'bg-teal-50',
    accent: 'text-teal-600',
  },
  { 
    headline: 'Knows when to step back',
    detail: 'Smooth handoff to a human agent the moment a conversation needs it.',
    bg: 'bg-rose-50',
    accent: 'text-rose-600',
  },
];

const ToolLogo = ({ name, slug, color, domain, localLogo }) => {
  const [stage, setStage] = useState(0);
  const hex = color.replace('#', '');

  // stage 0 = localLogo or clearbit, 1 = simpleicons, 2 = letter fallback
  const src = localLogo && stage === 0
    ? localLogo
    : stage === 0
    ? `https://logo.clearbit.com/${domain}`
    : `https://cdn.simpleicons.org/${slug}/${hex}`;

  return (
    <div className="flex flex-col items-center gap-2.5 group">
      <div className="bg-white rounded-2xl border border-[#E7E5E4] w-16 h-16 flex items-center justify-center shadow-sm group-hover:shadow-md group-hover:border-[#D1D5DB] transition-all duration-200">
        {stage === 2 ? (
          <span className="text-xl font-bold" style={{ color }}>{name[0]}</span>
        ) : (
          <img
            src={src}
            alt={name}
            className="w-10 h-10 object-contain"
            onError={() => setStage(s => s + 1)}
          />
        )}
      </div>
      <span className="text-xs font-medium text-[#78716C] text-center">{name}</span>
    </div>
  );
};

const ToolsSection = () => {
  return (
    <section id="tools" data-testid="tools-section" className="py-20 md:py-28 bg-[#FAFAF9]">
      <div className="max-w-5xl mx-auto px-6 md:px-12">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="text-[#2563EB] font-semibold text-sm uppercase tracking-wider">
            Tools
          </span>
          <h2 className="mt-4 font-['DM_Sans'] text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#1C1917] tracking-tight">
            Plug in. Connect. Convert.
          </h2>
          <p className="mt-4 text-[#57534E] text-base md:text-lg max-w-xl mx-auto">
            Works with the platforms your D2C brand already runs on —
            no rip-and-replace, no complex setup.
          </p>
        </motion.div>

        {/* Logo grid */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap justify-center gap-6 sm:gap-8 mb-8"
        >
          {tools.map((tool, i) => (
            <motion.div
              key={tool.name}
              initial={{ opacity: 0, scale: 0.85 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
            >
              <ToolLogo name={tool.name} slug={tool.slug} color={tool.color} domain={tool.domain} localLogo={tool.localLogo} />
            </motion.div>
          ))}

          {/* "and many more" tile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: tools.length * 0.04 }}
            className="flex flex-col items-center gap-2.5"
          >
            <div className="bg-[#EFF6FF] rounded-2xl border border-[#BFDBFE] w-16 h-16 flex items-center justify-center">
              <span className="text-[#2563EB] text-2xl font-bold leading-none">+</span>
            </div>
            <span className="text-xs font-semibold text-[#2563EB] text-center">many more</span>
          </motion.div>
        </motion.div>

        {/* Divider */}
        <div className="border-t border-[#E7E5E4] my-14" />

        {/* Capabilities */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h3 className="font-['DM_Sans'] text-2xl sm:text-3xl font-bold text-[#1C1917]">
            One agent. Every task.
          </h3>
          <p className="mt-2 text-[#78716C] text-sm">
            From first call to final confirmation — it handles the whole journey.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {capabilities.map((cap, i) => (
            <motion.div
              key={cap.headline}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              className={`${cap.bg} rounded-2xl p-5 border border-transparent hover:border-[#E7E5E4] transition-all duration-200 card-hover`}
            >
              <span className="text-2xl mb-3 block">{cap.emoji}</span>
              <p className={`font-['DM_Sans'] text-base font-semibold ${cap.accent} mb-1`}>
                {cap.headline}
              </p>
              <p className="text-[#57534E] text-sm leading-relaxed">{cap.detail}</p>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default ToolsSection;
